import React, { useState, useEffect } from "react";
import axios from "axios";
import { Database, Plus, Trash2, Edit2, CheckCircle2, Wifi, MapPin, CreditCard, X } from "lucide-react";

export const MasterDataPage = () => {
  const [activeTab, setActiveTab] = useState("paket"); // paket, odp, metode
  const [pakets, setPakets] = useState([]);
  const [odps, setOdps] = useState([]);
  const [metodes, setMetodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState("");

  // Modals
  const [showPaketModal, setShowPaketModal] = useState(false);
  const [paketForm, setPaketForm] = useState({
    nama: "",
    kecepatan: "20 Mbps",
    download_speed: 20,
    upload_speed: 20,
    harga: 200000,
    mikrotik_profile: "20M-UP-20M-DOWN",
    deskripsi: "",
    status: "aktif"
  });

  const [showOdpModal, setShowOdpModal] = useState(false);
  const [odpForm, setOdpForm] = useState({
    kode: "",
    nama: "",
    area: "",
    lat: -6.8732,
    lng: 107.6190,
    kapasitas: 8,
    olt_uplink: "OLT-ZTE-C320-P1"
  });

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

  const fetchAll = async () => {
    try {
      setLoading(true);
      const [rPak, rOdp, rMet] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/master/paket`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/master/odp`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/master/metode-bayar`, { withCredentials: true })
      ]);
      setPakets(rPak.data);
      setOdps(rOdp.data);
      setMetodes(rMet.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const handleSavePaket = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${BACKEND_URL}/api/master/paket`, paketForm, { withCredentials: true });
      setShowPaketModal(false);
      setNotification("Paket internet baru berhasil ditambahkan!");
      fetchAll();
      setTimeout(() => setNotification(""), 4000);
    } catch (err) {
      alert("Gagal menambah paket");
    }
  };

  const handleDeletePaket = async (id) => {
    if (window.confirm("Hapus paket internet ini?")) {
      try {
        await axios.delete(`${BACKEND_URL}/api/master/paket/${id}`, { withCredentials: true });
        fetchAll();
      } catch (err) {
        alert("Gagal menghapus");
      }
    }
  };

  const handleSaveOdp = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${BACKEND_URL}/api/master/odp`, odpForm, { withCredentials: true });
      setShowOdpModal(false);
      setNotification("ODP baru berhasil ditambahkan!");
      fetchAll();
      setTimeout(() => setNotification(""), 4000);
    } catch (err) {
      alert("Gagal menambah ODP");
    }
  };

  const handleDeleteOdp = async (id) => {
    if (window.confirm("Hapus ODP ini?")) {
      try {
        await axios.delete(`${BACKEND_URL}/api/master/odp/${id}`, { withCredentials: true });
        fetchAll();
      } catch (err) {
        alert("Gagal menghapus");
      }
    }
  };

  return (
    <div className="space-y-6" data-testid="master-data-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl">
        <div>
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 rounded bg-indigo-500/20 text-indigo-400 text-[10px] font-bold uppercase tracking-wider">
              System Master Data
            </span>
            <span className="text-xs text-slate-400">Paket, ODP & Metode Bayar</span>
          </div>
          <h1 className="text-xl sm:text-2xl font-black text-white mt-1">
            Master Data Aplikasi
          </h1>
          <p className="text-xs text-slate-400 mt-0.5">
            Konfigurasi paket kecepatan internet, data ODP fiber optik dan kanal pembayaran resmi.
          </p>
        </div>

        {/* Tab switcher */}
        <div className="flex bg-slate-900 border border-slate-700 rounded-xl p-1 text-xs">
          <button
            onClick={() => setActiveTab("paket")}
            data-testid="tab-paket"
            className={`px-3 py-1.5 rounded-lg font-semibold flex items-center gap-1.5 transition-colors ${
              activeTab === "paket" ? "bg-indigo-600 text-white" : "text-slate-400 hover:text-white"
            }`}
          >
            <Wifi className="w-3.5 h-3.5" />
            <span>Paket Internet</span>
          </button>
          <button
            onClick={() => setActiveTab("odp")}
            data-testid="tab-odp"
            className={`px-3 py-1.5 rounded-lg font-semibold flex items-center gap-1.5 transition-colors ${
              activeTab === "odp" ? "bg-amber-600 text-white" : "text-slate-400 hover:text-white"
            }`}
          >
            <MapPin className="w-3.5 h-3.5" />
            <span>Area & ODP</span>
          </button>
          <button
            onClick={() => setActiveTab("metode")}
            data-testid="tab-metode"
            className={`px-3 py-1.5 rounded-lg font-semibold flex items-center gap-1.5 transition-colors ${
              activeTab === "metode" ? "bg-cyan-600 text-white" : "text-slate-400 hover:text-white"
            }`}
          >
            <CreditCard className="w-3.5 h-3.5" />
            <span>Metode Bayar</span>
          </button>
        </div>
      </div>

      {notification && (
        <div className="p-3 bg-indigo-500/10 border border-indigo-500/30 text-indigo-300 rounded-xl text-xs flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-indigo-400" />
          <span>{notification}</span>
        </div>
      )}

      {/* Tab 1: Paket Internet */}
      {activeTab === "paket" && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-sm font-bold text-white">Daftar Paket Internet & Profil Mikrotik</h2>
            <button
              onClick={() => setShowPaketModal(true)}
              data-testid="add-paket-btn"
              className="px-3.5 py-1.5 bg-indigo-600 hover:bg-indigo-500 text-white text-xs font-bold rounded-xl flex items-center gap-1.5"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Tambah Paket</span>
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {pakets.map((p) => (
              <div
                key={p.id}
                className="p-4 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl flex flex-col justify-between space-y-3"
              >
                <div>
                  <div className="flex justify-between items-start">
                    <span className="px-2 py-0.5 rounded bg-indigo-500/20 text-indigo-300 text-[10px] font-bold">
                      {p.kecepatan}
                    </span>
                    <button
                      onClick={() => handleDeletePaket(p.id)}
                      className="text-slate-500 hover:text-rose-400 p-1"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  <h3 className="text-base font-bold text-white mt-1.5">{p.nama}</h3>
                  <p className="text-xl font-mono font-black text-cyan-400 mt-1">
                    Rp {Number(p.harga).toLocaleString("id-ID")}
                    <span className="text-xs text-slate-400 font-sans font-normal"> /bln</span>
                  </p>
                  <p className="text-[11px] text-slate-400 mt-2">{p.deskripsi}</p>
                </div>

                <div className="p-2 bg-slate-900 rounded-lg text-[10px] font-mono text-slate-400">
                  Rate Profile: <span className="text-indigo-300">{p.mikrotik_profile}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tab 2: ODP */}
      {activeTab === "odp" && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="text-sm font-bold text-white">Daftar Box ODP (Optical Distribution Point)</h2>
            <button
              onClick={() => setShowOdpModal(true)}
              data-testid="add-odp-btn"
              className="px-3.5 py-1.5 bg-amber-600 hover:bg-amber-500 text-white text-xs font-bold rounded-xl flex items-center gap-1.5"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>Tambah ODP</span>
            </button>
          </div>

          <div className="p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl overflow-hidden">
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-slate-800 text-slate-400 font-semibold">
                  <th className="py-2.5 px-3">Kode ODP</th>
                  <th className="py-2.5 px-3">Nama Titik & Area</th>
                  <th className="py-2.5 px-3">Koordinat (Lat, Lng)</th>
                  <th className="py-2.5 px-3">Port Terpakai</th>
                  <th className="py-2.5 px-3">Uplink OLT</th>
                  <th className="py-2.5 px-3 text-right">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/60">
                {odps.map((o) => (
                  <tr key={o.id} className="hover:bg-slate-900/40">
                    <td className="py-3 px-3 font-mono font-bold text-amber-400">{o.kode}</td>
                    <td className="py-3 px-3">
                      <p className="font-bold text-white">{o.nama}</p>
                      <span className="text-[10px] text-slate-400">Area: {o.area}</span>
                    </td>
                    <td className="py-3 px-3 font-mono text-slate-300 text-[11px]">
                      {o.lat}, {o.lng}
                    </td>
                    <td className="py-3 px-3 font-bold text-slate-200">
                      {o.terpakai} / {o.kapasitas} Port
                    </td>
                    <td className="py-3 px-3 text-slate-400">{o.olt_uplink}</td>
                    <td className="py-3 px-3 text-right">
                      <button
                        onClick={() => handleDeleteOdp(o.id)}
                        className="p-1.5 text-rose-400 hover:bg-rose-950/60 rounded-lg"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Tab 3: Metode Bayar */}
      {activeTab === "metode" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {metodes.map((m) => (
            <div
              key={m.id}
              className="p-5 rounded-2xl bg-[#0e1628] border border-slate-800 shadow-xl space-y-2"
            >
              <div className="flex justify-between items-start">
                <h3 className="text-sm font-bold text-white">{m.nama_bank}</h3>
                <span className="px-2 py-0.5 rounded bg-cyan-500/20 text-cyan-300 text-[10px] font-bold uppercase">
                  {m.kategori}
                </span>
              </div>
              <p className="text-lg font-mono font-bold text-cyan-400">{m.no_rekening}</p>
              <p className="text-xs text-slate-300">a/n {m.atas_nama}</p>
              <p className="text-[11px] text-slate-400 pt-1">{m.instruksi}</p>
            </div>
          ))}
        </div>
      )}

      {/* Add Paket Modal */}
      {showPaketModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#0f172a] border border-slate-700 rounded-2xl w-full max-w-md p-5 space-y-4">
            <div className="flex justify-between items-center border-b border-slate-800 pb-3">
              <h3 className="text-sm font-bold text-white">Tambah Paket Internet Baru</h3>
              <button onClick={() => setShowPaketModal(false)} className="text-slate-400">
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={handleSavePaket} className="space-y-3">
              <div>
                <label className="block text-xs text-slate-300 mb-1">Nama Paket *</label>
                <input
                  type="text"
                  required
                  value={paketForm.nama}
                  onChange={(e) => setPaketForm({ ...paketForm, nama: e.target.value })}
                  placeholder="Contoh: Ultra Stream 50M"
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-300 mb-1">Kecepatan</label>
                  <input
                    type="text"
                    required
                    value={paketForm.kecepatan}
                    onChange={(e) => setPaketForm({ ...paketForm, kecepatan: e.target.value })}
                    placeholder="50 Mbps"
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-300 mb-1">Harga / Bulan (Rp)</label>
                  <input
                    type="number"
                    required
                    value={paketForm.harga}
                    onChange={(e) => setPaketForm({ ...paketForm, harga: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100 font-mono"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-slate-300 mb-1">Profil Mikrotik Rate Limit</label>
                <input
                  type="text"
                  required
                  value={paketForm.mikrotik_profile}
                  onChange={(e) => setPaketForm({ ...paketForm, mikrotik_profile: e.target.value })}
                  placeholder="50M-UP-50M-DOWN"
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100 font-mono"
                />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowPaketModal(false)}
                  className="px-3 py-1.5 bg-slate-800 text-slate-300 rounded-xl text-xs"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  className="px-4 py-1.5 bg-indigo-600 text-white rounded-xl text-xs font-bold"
                >
                  Simpan Paket
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add ODP Modal */}
      {showOdpModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-[#0f172a] border border-slate-700 rounded-2xl w-full max-w-md p-5 space-y-4">
            <div className="flex justify-between items-center border-b border-slate-800 pb-3">
              <h3 className="text-sm font-bold text-white">Tambah ODP Box Baru</h3>
              <button onClick={() => setShowOdpModal(false)} className="text-slate-400">
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={handleSaveOdp} className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-300 mb-1">Kode ODP *</label>
                  <input
                    type="text"
                    required
                    value={odpForm.kode}
                    onChange={(e) => setOdpForm({ ...odpForm, kode: e.target.value })}
                    placeholder="ODP-BDG-06"
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100 font-mono"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-300 mb-1">Area Lokasi *</label>
                  <input
                    type="text"
                    required
                    value={odpForm.area}
                    onChange={(e) => setOdpForm({ ...odpForm, area: e.target.value })}
                    placeholder="Contoh: Dago"
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs text-slate-300 mb-1">Nama / Titik Tiang ODP *</label>
                <input
                  type="text"
                  required
                  value={odpForm.nama}
                  onChange={(e) => setOdpForm({ ...odpForm, nama: e.target.value })}
                  placeholder="ODP Tiang 45 Dago Asri"
                  className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-slate-300 mb-1">Latitude</label>
                  <input
                    type="number"
                    step="0.0001"
                    value={odpForm.lat}
                    onChange={(e) => setOdpForm({ ...odpForm, lat: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100 font-mono"
                  />
                </div>
                <div>
                  <label className="block text-xs text-slate-300 mb-1">Longitude</label>
                  <input
                    type="number"
                    step="0.0001"
                    value={odpForm.lng}
                    onChange={(e) => setOdpForm({ ...odpForm, lng: parseFloat(e.target.value) || 0 })}
                    className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded-xl text-xs text-slate-100 font-mono"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowOdpModal(false)}
                  className="px-3 py-1.5 bg-slate-800 text-slate-300 rounded-xl text-xs"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  className="px-4 py-1.5 bg-amber-600 text-white rounded-xl text-xs font-bold"
                >
                  Simpan ODP
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
